/**
 * 🔒 P1 보안 개선: 보안 이벤트 로깅 서비스
 *
 * 무단 접근 시도, 권한 거부, 보안 관련 이벤트를 기록하는 서비스
 * Firestore의 securityLogs 컬렉션에 저장
 */

import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { Permission } from '../types/auth';
import { logger } from '../utils/logger.ts';

// 보안 이벤트 타입
export type SecurityEventType =
  | 'UNAUTHORIZED_ACCESS'     // 권한 없는 접근 시도
  | 'PERMISSION_DENIED'       // 권한 거부
  | 'ROUTE_ACCESS_DENIED'     // 라우트 접근 거부
  | 'API_ACCESS_DENIED'       // API 호출 권한 거부
  | 'SUSPICIOUS_ACTIVITY'     // 의심스러운 활동
  | 'LOGIN_FAILED'            // 로그인 실패
  | 'RATE_LIMIT_EXCEEDED'     // 요청 제한 초과
  | 'DATA_ACCESS_VIOLATION';  // 데이터 접근 위반

// 보안 이벤트 심각도
export type SecuritySeverity = 'low' | 'medium' | 'high' | 'critical';

// 보안 로그 인터페이스
export interface SecurityLog {
  id?: string;
  eventType: SecurityEventType;
  severity: SecuritySeverity;
  userId: string;
  userEmail?: string;
  userRole?: string;
  attemptedResource: string;
  missingPermissions: Permission[];
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, unknown>;
  resolved?: boolean;
  resolvedAt?: Date;
  resolvedBy?: string;
}

// 필터 옵션
export interface SecurityLogFilter {
  userId?: string;
  eventType?: SecurityEventType;
  severity?: SecuritySeverity;
  startDate?: Date;
  endDate?: Date;
  resolved?: boolean;
  limit?: number;
}

class SecurityLogService {
  private collectionName = 'securityLogs';

  /**
   * 심각도 결정 로직
   */
  private determineSeverity(
    eventType: SecurityEventType,
    missingPermissions: Permission[]
  ): SecuritySeverity {
    // 관리자 권한 관련 이벤트는 높은 심각도
    const adminPermissions: Permission[] = ['manage_users', 'edit_settings', 'manage_leave_settings'];
    const hasAdminPermission = missingPermissions.some(p => adminPermissions.includes(p));

    if (eventType === 'SUSPICIOUS_ACTIVITY' || eventType === 'DATA_ACCESS_VIOLATION') {
      return 'critical';
    }

    if (hasAdminPermission) {
      return 'high';
    }

    if (eventType === 'LOGIN_FAILED' || eventType === 'RATE_LIMIT_EXCEEDED') {
      return 'medium';
    }

    return 'low';
  }

  /**
   * 보안 이벤트 로그 기록
   */
  async logSecurityEvent(
    eventType: SecurityEventType,
    userId: string,
    attemptedResource: string,
    missingPermissions: Permission[] = [],
    details?: Record<string, unknown>
  ): Promise<void> {
    try {
      const severity = this.determineSeverity(eventType, missingPermissions);

      const logData = {
        eventType,
        severity,
        userId,
        userEmail: details?.userEmail || null,
        userRole: details?.userRole || null,
        attemptedResource,
        missingPermissions,
        timestamp: Timestamp.fromDate(new Date()),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
        details: details || {},
        resolved: false
      };

      await addDoc(collection(db, this.collectionName), logData);

      // 높은 심각도 이벤트는 콘솔에도 경고
      if (severity === 'high' || severity === 'critical') {
        logger.warn(`[SecurityLogService] ${severity.toUpperCase()} security event: ${eventType}`, {
          context: {
            userId,
            attemptedResource,
            missingPermissions
          }
        });
      } else {
        logger.debug(`[SecurityLogService] Security event logged: ${eventType}`);
      }
    } catch (error) {
      logger.error('[SecurityLogService] Failed to log security event:', error instanceof Error ? error : undefined);
      // 로깅 실패가 메인 로직을 방해하지 않도록 에러를 던지지 않음
    }
  }

  /**
   * 무단 접근 시도 로그
   */
  async logUnauthorizedAccess(
    userId: string,
    attemptedResource: string,
    missingPermissions: Permission[] = [],
    details?: Record<string, unknown>
  ): Promise<void> {
    await this.logSecurityEvent(
      'UNAUTHORIZED_ACCESS',
      userId,
      attemptedResource,
      missingPermissions,
      details
    );
  }

  /**
   * 라우트 접근 거부 로그
   */
  async logRouteAccessDenied(
    userId: string,
    route: string,
    missingPermissions: Permission[] = [],
    details?: Record<string, unknown>
  ): Promise<void> {
    await this.logSecurityEvent(
      'ROUTE_ACCESS_DENIED',
      userId,
      route,
      missingPermissions,
      details
    );
  }

  /**
   * 로그인 실패 로그
   */
  async logLoginFailed(
    email: string,
    reason: string,
    details?: Record<string, unknown>
  ): Promise<void> {
    await this.logSecurityEvent(
      'LOGIN_FAILED',
      'anonymous',
      'LOGIN',
      [],
      {
        ...details,
        userEmail: email,
        reason
      }
    );
  }

  /**
   * 의심스러운 활동 로그
   */
  async logSuspiciousActivity(
    userId: string,
    activity: string,
    details?: Record<string, unknown>
  ): Promise<void> {
    await this.logSecurityEvent(
      'SUSPICIOUS_ACTIVITY',
      userId,
      activity,
      [],
      details
    );
  }

  /**
   * 보안 로그 조회 (관리자 전용)
   */
  async getSecurityLogs(filter: SecurityLogFilter = {}): Promise<SecurityLog[]> {
    try {
      const {
        userId,
        eventType,
        severity,
        startDate,
        endDate,
        resolved,
        limit: queryLimit = 100
      } = filter;

      // 기본 쿼리
      let q = query(
        collection(db, this.collectionName),
        orderBy('timestamp', 'desc'),
        limit(queryLimit)
      );

      // 필터 적용
      if (userId) {
        q = query(q, where('userId', '==', userId));
      }
      if (eventType) {
        q = query(q, where('eventType', '==', eventType));
      }
      if (severity) {
        q = query(q, where('severity', '==', severity));
      }
      if (resolved !== undefined) {
        q = query(q, where('resolved', '==', resolved));
      }
      if (startDate) {
        q = query(q, where('timestamp', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('timestamp', '<=', Timestamp.fromDate(endDate)));
      }

      const querySnapshot = await getDocs(q);
      const logs: SecurityLog[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date(),
          resolvedAt: data.resolvedAt?.toDate()
        } as SecurityLog);
      });

      return logs;
    } catch (error) {
      logger.error('[SecurityLogService] Failed to get security logs:', error instanceof Error ? error : undefined);
      return [];
    }
  }

  /**
   * 미해결 보안 이벤트 수 조회
   */
  async getUnresolvedCount(): Promise<number> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('resolved', '==', false)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      logger.error('[SecurityLogService] Failed to get unresolved count:', error instanceof Error ? error : undefined);
      return 0;
    }
  }

  /**
   * 심각도별 통계 조회
   */
  async getStatsBySeverity(): Promise<Record<SecuritySeverity, number>> {
    const stats: Record<SecuritySeverity, number> = {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    };

    try {
      const logs = await this.getSecurityLogs({ limit: 1000 });
      logs.forEach(log => {
        if (log.severity in stats) {
          stats[log.severity]++;
        }
      });
    } catch (error) {
      logger.error('[SecurityLogService] Failed to get stats:', error instanceof Error ? error : undefined);
    }

    return stats;
  }
}

// 싱글톤 인스턴스 export
export const securityLogService = new SecurityLogService();

export default securityLogService;
